import CustomToast from "../../utilities/components/CustomToast";
import HttpClient from "../../utilities/functions/HttpClient";
import ServerEndpoints from "../../utilities/constants/ServerEndPoints";
import Errors from "../../utilities/constants/Errors";
import KeycloakService from "../config/KeycloakService";

/**
 * Fetch all data from the backend.
 * @param {Function} setEventsData The function to collect and set the data from the database.
 */
export const getAllEvents = async (setEventsData) => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.EVENTS_ENDPOINT}`,
    );
    if (response.status === 200) {
      setEventsData(response.data);
    } else {
      // handle non-200 responses
    }
  } catch (err) {
    if (!err.response) {
      // Handle network errors here
      CustomToast(Errors.NETWORK_ERROR, "error");
    } else {
      // Handle HTTP errors
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          break;

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    }
  }
};

/**
 * Retrieve a single event from the database.
 * @param {Function} setEventData The function to collect the fetched data.
 * @param {number} id The event's id to fetch.
 */
/**
 * Retrieve a single event from the database with aggregated reaction and comment stats.
 * @param {Function} setEventData The function to collect the fetched data.
 * @param {number} id The event's id to fetch.
 */
export const getEventById = async (setEventData, id) => {
  try {
    // 1. Fetch Event Details
    const eventReq = HttpClient.getAxiosClient().get(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${id}`,
    );

    // 2. Fetch Comments (for count)
    const commentsReq = HttpClient.getAxiosClient().get(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${id}/comments`,
    );

    // 3. Fetch Like Count (active count only, page 0 size 1 to get totalElements)
    const likeCountReq = HttpClient.getAxiosClient().get(
      `${ServerEndpoints.REACTIONS_ENDPOINT}/by-target`,
      {
        params: {
          targetType: "EVENT",
          targetId: id,
          reactionType: "LIKE",
          page: 0,
          size: 1,
        },
      },
    );

    // 4. Fetch Dislike Count
    const dislikeCountReq = HttpClient.getAxiosClient().get(
      `${ServerEndpoints.REACTIONS_ENDPOINT}/by-target`,
      {
        params: {
          targetType: "EVENT",
          targetId: id,
          reactionType: "DISLIKE",
          page: 0,
          size: 1,
        },
      },
    );

    // 5. Fetch Current User Reaction (if logged in)
    let userReactionReq = Promise.resolve(null);
    if (KeycloakService.isLoggedIn()) {
      userReactionReq = HttpClient.getAxiosClient()
        .get(`${ServerEndpoints.REACTIONS_ENDPOINT}/user`, {
          params: {
            userId: KeycloakService.getUserId(),
            targetType: "EVENT",
            targetId: id,
          },
        })
        .catch(() => null); // Handle 404 (no reaction) gracefully
    }

    // Execute all requests in parallel
    const [eventRes, commentsRes, likeRes, dislikeRes, userReactionRes] =
      await Promise.all([
        eventReq,
        commentsReq,
        likeCountReq,
        dislikeCountReq,
        userReactionReq,
      ]);

    if (eventRes.status === 200) {
      const event = eventRes.data;

      // Aggregate Data
      event.commentsCount =
        commentsRes.status === 200 ? commentsRes.data.length : 0;
      event.likeCount = likeRes.status === 200 ? likeRes.data.totalElements : 0;
      event.dislikeCount =
        dislikeRes.status === 200 ? dislikeRes.data.totalElements : 0;
      event.currentUserReaction =
        userReactionRes &&
        userReactionRes.status === 200 &&
        userReactionRes.data
          ? userReactionRes.data.reactionType
          : null;

      setEventData(event);
    }
  } catch (err) {
    if (err.response) {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          break;
        case 404:
          CustomToast(
            "The event you are trying to retrieve does not exist in the database.",
            "error",
          );
          break;
        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    } else {
      CustomToast(Errors.NETWORK_ERROR, "error");
    }
  }
};

/**
 * Fetch all media related to an event from the backend.
 * @param {Function} setMediaData The function to collect and set the data from the database.
 * @param {number} id The id of the targeted event.
 * @returns The media data.
 * @throws {Error} The error message.
 */
export const getMediaByEventId = async (setMediaData, id) => {
  await HttpClient.getAxiosClient()
    .get(`${ServerEndpoints.EVENTS_ENDPOINT}/${id}/media`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    })
    .then((data) => {
      setMediaData(data);
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          break;

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The media you are trying to retrieve does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Delete an single event.
 * @param {number} id The targeted event identifier.
 * @param {Function} setState The function to update the flag status for the page refresh.
 */
export const deleteEvent = async (id, setState) => {
  await HttpClient.getAxiosClient()
    .delete(`${ServerEndpoints.EVENTS_ENDPOINT}/${id}`)
    .then((response) => {
      if (response.status === 204) {
        CustomToast("Event successfully deleted", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The event you are trying to delete does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Update an event in the database.
 * @param {Object} event The updated information of the targeted event.
 */
export const updateEvent = async (event, media) => {
  const requestData = new FormData();
  const arrayOfFiles = [...media];
  requestData.append(
    "event",
    new Blob([JSON.stringify(event)], { type: "application/json" }),
  );

  arrayOfFiles.forEach((file) => requestData.append("media", file));
  await HttpClient.getAxiosClient()
    .put(`${ServerEndpoints.EVENTS_ENDPOINT}/${event.id}`, requestData)
    .then((response) => {
      if (response.status === 200) {
        CustomToast("Event successfully updated", "success");
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The event you are trying to update does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Save an event in the database.
 * @param {Object} event The updated information of the targeted event.
 */
export const createEvent = async (event, media) => {
  const requestData = new FormData();
  const arrayOfFiles = [...media];
  requestData.append(
    "event",
    new Blob([JSON.stringify(event)], { type: "application/json" }),
  );

  arrayOfFiles.forEach((file) => requestData.append("media", file));

  await HttpClient.getAxiosClient()
    .post(`${ServerEndpoints.EVENTS_ENDPOINT}`, requestData)
    .then((response) => {
      if (response.status === 201) {
        CustomToast("Event successfully created", "success");
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 409:
          CustomToast("An event with the provided id already exist", "error");
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

// Comments services

/**
 * Fetch all comments related to an event from the backend.
 * @param {Function} setEventsData The function to collect and set the data from the database.
 * @param {number} eventId The id of the targeted event.
 */
export const getCommentsByEventId = async (setEventsData, eventId) => {
  try {
    const response = await HttpClient.getAxiosClient().get(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${eventId}/comments`,
    );

    if (response.status === 200) {
      const comments = response.data;

      // Fetch reactions for each comment
      const enrichedComments = await Promise.all(
        comments.map(async (comment) => {
          const likeCountReq = HttpClient.getAxiosClient()
            .get(`${ServerEndpoints.REACTIONS_ENDPOINT}/by-target`, {
              params: {
                targetType: "COMMENT",
                targetId: comment.id,
                reactionType: "LIKE",
                page: 0,
                size: 1,
              },
            })
            .catch(() => ({ data: { totalElements: 0 } }));

          const dislikeCountReq = HttpClient.getAxiosClient()
            .get(`${ServerEndpoints.REACTIONS_ENDPOINT}/by-target`, {
              params: {
                targetType: "COMMENT",
                targetId: comment.id,
                reactionType: "DISLIKE",
                page: 0,
                size: 1,
              },
            })
            .catch(() => ({ data: { totalElements: 0 } }));

          let userReactionReq = Promise.resolve(null);
          if (KeycloakService.isLoggedIn()) {
            userReactionReq = HttpClient.getAxiosClient()
              .get(`${ServerEndpoints.REACTIONS_ENDPOINT}/user`, {
                params: {
                  userId: KeycloakService.getUserId(),
                  targetType: "COMMENT",
                  targetId: comment.id,
                },
              })
              .catch(() => null);
          }

          const [likeRes, dislikeRes, userReactionRes] = await Promise.all([
            likeCountReq,
            dislikeCountReq,
            userReactionReq,
          ]);

          return {
            ...comment,
            likeCount: likeRes?.data?.totalElements ?? 0,
            dislikeCount: dislikeRes?.data?.totalElements ?? 0,
            currentUserReaction:
              userReactionRes?.status === 200 && userReactionRes.data
                ? userReactionRes.data.reactionType
                : null,
          };
        }),
      );

      setEventsData(enrichedComments);
    }
  } catch (err) {
    if (err.response) {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          break;
        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;
        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    } else {
      CustomToast(Errors.NETWORK_ERROR, "error");
    }
  }
};

/**
 * Fetch all comments related to an event from the backend.
 * @param {Function} setRepliesData The function to collect and set the data from the database.
 * @param {number} eventId The id of the targeted event.
 * @param {number} baseCommentId The id of the targeted baseComment.
 */
export const getReplies = async (setRepliesData, eventId, baseCommentId) => {
  await HttpClient.getAxiosClient()
    .get(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${eventId}/comments/${baseCommentId}/replies`,
    )
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    })
    .then((data) => setRepliesData(data))
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Retrieve a single eventComment from the database.
 * @param {Function} setEventData The function to collect the fetched data.
 * @param {number} id The eventComment's id to fetch.
 * @param {number} eventId The id of the targeted event.
 */
export const getEventCommentById = async (setEventData, id, eventId) => {
  await HttpClient.getAxiosClient()
    .get(`${ServerEndpoints.EVENTS_ENDPOINT}/${eventId}/comments/${id}`)
    .then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    })
    .then((data) => setEventData(data))
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The comment you are trying to retrieve does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Delete an single event.
 * @param {number} id The targeted eventComment identifier.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {number} eventId The id of the targeted event.
 */
export const deleteEventComment = async (id, setState, eventId) => {
  await HttpClient.getAxiosClient()
    .delete(`${ServerEndpoints.EVENTS_ENDPOINT}/${eventId}/comments/${id}`)
    .then((response) => {
      if (response.status === 204) {
        CustomToast("Event successfully deleted", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The comment you are trying to delete does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Update an eventComment in the database.
 * @param {Object} eventComment The updated information of the targeted eventComment.
 */
export const updateEventComment = async (eventComment) => {
  await HttpClient.getAxiosClient()
    .put(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${eventComment.event.id}/comments/${eventComment.id}`,
      eventComment,
    )
    .then((response) => {
      if (response.status === 200) {
        CustomToast("Comment successfully updated", "success");
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The comment you are trying to update does not exist in the database.",
            "error",
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Save an eventComment in the database.
 * @param {Object} eventComment The newly created information of the targeted eventComment.
 */
export const createEventComment = async (eventComment, setRefresh) => {
  await HttpClient.getAxiosClient()
    .post(
      `${ServerEndpoints.EVENTS_ENDPOINT}/${eventComment.eventId}/comments`,
      eventComment,
    )
    .then((response) => {
      if (response.status === 201) {
        CustomToast("Thank you for sharing your thought!", "success");
        setRefresh((prev) => !prev);
      }
    })
    .catch((err) => {
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVAILABLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 409:
          CustomToast("A comment with the provided id already exist", "error");
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    });
};

/**
 * Toggle a reaction (LIKE/DISLIKE) for a target (EVENT/COMMENT).
 * @param {string} userId The user identifier.
 * @param {string} targetType The target type (EVENT, COMMENT, REVIEW).
 * @param {string} targetId The target identifier.
 * @param {string} reactionType The reaction type (LIKE, DISLIKE).
 * @param {Function} setRefresh Optional function to refresh parent state.
 */
export const toggleReaction = async (
  userId,
  targetType,
  targetId,
  reactionType,
  setRefresh,
) => {
  // UserReactionDTO structure
  const reactionDTO = {
    userId,
    targetType,
    targetId,
    reactionType,
  };

  await HttpClient.getAxiosClient()
    .post(`${ServerEndpoints.REACTIONS_ENDPOINT}/toggle`, reactionDTO)
    .then((response) => {
      if (response.status === 200 || response.status === 204) {
        const action = reactionType === "LIKE" ? "liked" : "disliked";
        // If status is 204, it means the reaction was removed (untoggled)
        if (response.status === 204) {
          CustomToast(`Reaction removed`, "info");
        } else {
          CustomToast(
            `You ${action} this ${targetType.toLowerCase()}!`,
            "success",
          );
        }
        if (setRefresh) setRefresh((prev) => !prev);
      }
    })
    .catch((err) => {
      if (err.response) {
        if (err.response.status === 404) {
          CustomToast("Content not found", "error");
        } else if (err.response.status === 400) {
          CustomToast("Invalid reaction data. Please try again.", "error");
        } else {
          CustomToast(Errors.API_ERROR, "error");
        }
      } else {
        CustomToast(Errors.NETWORK_ERROR, "error");
      }
    });
};
